/**
 * Flag-emoji support detection + lazy fallback-font loading for HBCountryFlag.
 *
 * Why: a country flag is a plain Unicode regional-indicator pair (e.g. 🇺🇸 =
 * U+1F1FA U+1F1F8). Whether it becomes a flag glyph is entirely up to the OS/
 * browser emoji font. Windows ships no flag glyphs in Segoe UI Emoji, so Chrome
 * and Edge degrade every flag to its two-letter code ("US"). Apple/Noto render
 * real flags, so the ~83% of users on those platforms need nothing.
 *
 * Strategy: detect support once (canvas), and ONLY for the unsupported cohort,
 * lazily inject an @font-face for a flags-only woff2 hosted on hbstatic, so
 * supported users never download it. The @font-face is unicode-range-scoped to the
 * flag codepoints, and the family is applied solely on the HBCountryFlag span —
 * nothing else in the app can pick it up.
 */

/** Font family injected for the unsupported cohort. Flags-only, unicode-range scoped. */
export const FLAG_FONT_FAMILY = "'Twemoji Country Flags'";

/**
 * Every codepoint the fallback font covers: the regional indicators (country
 * flags), plus 🏴 (U+1F3F4) and the tag characters that form the England,
 * Scotland and Wales subdivision flags. Matches country-flag-emoji-polyfill.
 */
const FLAG_UNICODE_RANGE =
    'U+1F1E6-1F1FF, U+1F3F4, U+E0062-E0063, U+E0065, U+E0067, U+E006C, U+E006E, U+E0073-E0074, U+E0077, U+E007F';

/**
 * Flags-only subset of Twemoji (CC-BY 4.0), from country-flag-emoji-polyfill.
 * Versioned filename: upload a new version rather than overwriting this one.
 */
const FLAG_FONT_URL =
    'https://hbstatic.honeybook.com/assets_system/system_web/web_fonts/TwemojiCountryFlags-v1.woff2';

/** CC BY 4.0 credit for the font's artwork, shipped with the injected @font-face. */
const FLAG_FONT_ATTRIBUTION =
    'Flag emoji: Twemoji, (c) Twitter and other contributors, CC BY 4.0 (https://creativecommons.org/licenses/by/4.0/), https://github.com/jdecked/twemoji. Subset to flag glyphs.';

let cachedSupport: boolean | undefined;
let supportOverride: boolean | undefined;

/**
 * Forces the detection result, bypassing the canvas probe. Intended for tests
 * and Storybook/Chromatic, where we want to render both the native-emoji path
 * and the fallback-font path deterministically regardless of the host platform.
 * Pass `undefined` to restore real auto-detection. `false` also preloads the
 * fallback font so a snapshot of that path is stable.
 */
export function setFlagEmojiSupportOverride(value: boolean | undefined): void {
    supportOverride = value;
    if (value === false) {
        void ensureFlagFont();
    }
}

/**
 * True when the platform renders flag emoji as real flags. Result is computed
 * once and cached. On the server (no DOM) we assume support and never inject —
 * the client re-checks on mount.
 */
export function flagEmojiSupported(): boolean {
    if (supportOverride !== undefined) {
        return supportOverride;
    }
    if (cachedSupport !== undefined) {
        return cachedSupport;
    }
    cachedSupport = detectFlagEmojiSupport();
    return cachedSupport;
}

/**
 * Renders the US flag to a 1-glyph canvas and checks for saturated red AND blue
 * pixels. A real flag always has both; the two-letter "US" text fallback is
 * monochrome (greyscale even after anti-aliasing), so it satisfies neither.
 * This is robust where naive color-count checks fail on text anti-aliasing.
 */
function detectFlagEmojiSupport(): boolean {
    if (
        typeof document === 'undefined' ||
        typeof document.createElement !== 'function'
    ) {
        return true; // SSR / non-DOM — never inject on the server.
    }

    try {
        const size = 16;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
            return true; // Can't detect — assume support, do nothing.
        }

        ctx.textBaseline = 'middle';
        ctx.font = `${size}px sans-serif`;
        ctx.clearRect(0, 0, size, size);
        ctx.fillText('\u{1F1FA}\u{1F1F8}', 0, size / 2); // 🇺🇸

        const { data } = ctx.getImageData(0, 0, size, size);
        let hasRed = false;
        let hasBlue = false;
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];
            if (a === 0) {
                continue;
            }
            if (r > 100 && g < 80 && b < 80) {
                hasRed = true;
            }
            if (b > 100 && r < 80 && g < 100) {
                hasBlue = true;
            }
            if (hasRed && hasBlue) {
                return true;
            }
        }
        return false;
    } catch {
        return true; // Any detection failure — assume support, do nothing.
    }
}

let fontPromise: Promise<void> | undefined;

/**
 * Injects the flags-only @font-face exactly once and waits for the font to load.
 * Called only when {@link flagEmojiSupported} is false.
 */
export function ensureFlagFont(): Promise<void> {
    if (typeof document === 'undefined') {
        return Promise.resolve();
    }
    if (fontPromise) {
        return fontPromise;
    }

    fontPromise = (async () => {
        if (!document.querySelector('style[data-hb-country-flags]')) {
            const style = document.createElement('style');
            style.setAttribute('data-hb-country-flags', '');
            // The attribution lives inside the injected CSS (not a code comment) so
            // minifiers can't strip it; Twemoji accepts a credit in the HTML/JS source.
            style.textContent = `
                /* ${FLAG_FONT_ATTRIBUTION} */
                @font-face {
                    font-family: ${FLAG_FONT_FAMILY};
                    font-display: swap;
                    unicode-range: ${FLAG_UNICODE_RANGE};
                    src: url('${FLAG_FONT_URL}') format('woff2');
                }
            `;
            document.head.appendChild(style);
        }
        // Resolve only once the font is actually ready to paint, so a caller
        // can reveal a hidden flag without a flash of the two-letter code.
        // The face is unicode-range-scoped to flags, so load() must be probed
        // with a flag string — the default latin probe matches no glyph and
        // would skip loading entirely.
        if (document.fonts?.load) {
            await document.fonts.load(
                `1em ${FLAG_FONT_FAMILY}`,
                '\u{1F1FA}\u{1F1F8}'
            );
        }
    })().catch(() => {
        // Font failed to load — leave the native two-letter fallback in place.
        fontPromise = undefined;
    });

    return fontPromise;
}

/** Converts an ISO 3166-1 alpha-2 code to its regional-indicator flag emoji. */
export function codeToFlagEmoji(code: string): string {
    const REGIONAL_INDICATOR_A = 0x1f1e6;
    const ASCII_A = 'A'.charCodeAt(0);
    return code
        .toUpperCase()
        .replace(/[A-Z]/g, char =>
            String.fromCodePoint(
                REGIONAL_INDICATOR_A + char.charCodeAt(0) - ASCII_A
            )
        );
}
